<template>
  <div class="class-detail">
    <organization-breadcrumb :items="[getCurrentClass.name]" />
    <div class="organization-container">
      <div class="content">
        <el-menu
          :default-active="activeEntry"
          ref="menu"
          mode="horizontal"
          class="sidemenu filter-side-menu"
          @select="onMenuChange"
        >
          <el-menu-item index="1">
            <span>{{ $t("classes.classes_details.menu.overview") }}</span>
          </el-menu-item>
          <el-menu-item v-if="canEditClass" index="2" ref="studentsEntry">
            <span>{{ $t("classes.classes_details.menu.detailed_progress") }}</span>
          </el-menu-item>
          <el-menu-item v-if="canEditClass" index="3">
            <span>{{ $t("classes.classes_details.menu.time_tracking") }}</span>
          </el-menu-item>
          <el-menu-item v-if="canEditClass" index="4" >
            <span>{{ $t("classes.classes_details.menu.assignment") }}</span>
          </el-menu-item>
          <el-menu-item v-if="canEditClass && !isOrganizationK12" index="5" >
            <span>{{ $t("classes.classes_details.menu.assessment") }}</span>
          </el-menu-item>
          <el-menu-item v-if="canEditClass && isOrganizationK12 && isOrgAvailableForElsaAI" index="6" >
            <span>{{ $t("classes.classes_details.menu.ai_role_play_log") }}</span>
          </el-menu-item>
        </el-menu>
        <div class="component-wrapper">
          <component
            :currentClass="getCurrentClass"
            @edit="editMode = true"
            :groupId="classId"
            @showStudents="openStudents"
            @menuVisible="handleMenuVisibility"
            :is="activeComponent"
            :organizationType="currentOrganizationType"
            :hasSpecialAssessmentTest="hasSpecialAssessmentTest"
            :filterOptions="assessmentFilterOptions"
            @setGoals="editClass"
            :endDate="endDate"
            :startDate="startDate"
            :scope="scope"
            :timelineLabel="timelineLabel || this.$t('date_interval_picker.this_month')"
            @updatedDate="setNewDate"
            @updateScope="scope = $event"
            @goToTabIndex="goToTabIndex"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import StudentsIcon from "@/assets/images/inline.students.svg";
  import ReportsIcon from "@/assets/images/inline.reports.svg";
  import OverviewIcon from "@/assets/images/inline.overview.svg";
  import ClassCreateDetails from "@/components/classes/create-classes/ClassCreateDetails";
  import { mapGetters, mapActions } from "vuex";
  import ApiErrors from "@/components/common/ApiErrors";
  import { successMessage } from "@/utils/messages";
  import Overview from "@/components/classes/details/Overview";
  import ClassStudents from "@/components/classes/details/ClassStudents";
  import BackToList from "@/components/common/BackToList";
  import ClassReports from "@/components/classes/details/ClassReports";
  import { path, pathOr, pathEq } from "ramda";
  import { format, startOfMonth, toDate } from "date-fns";
  import { locale_mapping_date } from "@/helpers/languages";
  import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
  import AssignmentList from "@/components/classes/details/AssignmentList";
  import AssessmentList from "@/components/classes/details/AssessmentList";
  import ElsaSelect from '@/components/common/Select';
  import ConversationList from "@/components/classes/details/ConversationList.vue";
  import { orgsAvailableELSAAI } from "@/constants/elsaAI";

  export default {
    components: {
      StudentsIcon,
      BackToList,
      ReportsIcon,
      OverviewIcon,
      ClassCreateDetails,
      ApiErrors,
      Overview,
      ClassStudents,
      ClassReports,
      OrganizationBreadcrumb,
      AssignmentList,
      AssessmentList,
      ElsaSelect,
      ConversationList,
    },
    computed: {
      ...mapGetters('user', ['isElsaAdmin']),
      ...mapGetters({
        getCurrentClass: "classes/getCurrentClass",
        currentOrganizationType: "organization/currentOrganizationType",
        currentOrganizationId: "organization/currentOrganizationId",
        userIsTeacher: "organization/isOrganizationTeacher",
        userIsAdmin: "organization/isOrganizationAdmin",
        userIsAssociateAdmin: "organization/isOrganizationAssociateAdmin",
        currentUserId: "user/getUserId",
        getBuildingClass: "classes/getBuildingClass",
        currentOrganizationCode: "organization/currentOrganizationCode",
        organizationHasSpecialAssessmentTest: "organization/organizationHasSpecialAssessmentTest",
        isOrganizationK12: "organization/isOrganizationK12",
      }),
      isOwner() {
        const userId = this.currentUserId;
        return pathEq(["getCurrentClass", "owner", "id"], userId)(this);
      },
      canEditClass() {
        return this.userIsAdmin || this.userIsAssociateAdmin || (this.userIsTeacher && this.isOwner);
      },
      timelineLabel() {
        const formattedStartDate = format(new Date(this.startDate), "dd MMM yyyy", {
          locale: locale_mapping_date[this.$i18n.locale],
        });
        const formattedEndDate = format(new Date(this.endDate), "dd MMM yyyy", {
          locale: locale_mapping_date[this.$i18n.locale],
        });
        return `${formattedStartDate} - ${formattedEndDate}`;
      },
      assessmentFilterOptions() {
        const assessmentTypes =  [
          {
            label: this.$t('classes.classes_details.students_section.elsa_assessment'),
            value: "general",
          },
          {
            label: this.$t('classes.classes_details.students_section.mini_assessment'),
            value: "mini",
          },
        ]
        if(this.hasSpecialAssessmentTest) {
          assessmentTypes.unshift({
            label: this.currentOrganizationCode+' '+this.$t('classes.classes_details.students_section.assessment'),
            value: "sgd-assessment",
          })
        }
        return assessmentTypes
      },
      hasSpecialAssessmentTest() {
        return this.currentOrganizationCode!=null && this.organizationHasSpecialAssessmentTest
      },
      isOrgAvailableForElsaAI() {
        return orgsAvailableELSAAI.includes(this.currentOrganizationId);
      },
    },
    data() {
      return {
        activeEntry: this.$route.params.active_entry?this.$route.params.active_entry:"1",
        editMode: false,
        errors: {},
        activeComponent: this.$route.params.active_component?this.$route.params.active_component:"overview",
        isMenuVisible: true,
        startDate: startOfMonth(Date.now()),
        endDate: toDate(Date.now()),
        scope: "daily",
        // timelineLabel: '',
      };
    },
    props: {
      activeComponentProp: {},
      classId: {},
      organizationId: {},
    },
    async mounted() {
      if (this.activeComponentProp === "class-students") {
        this.activeComponent = this.activeComponentProp;
        return this.openStudents();
      }
      this.$store.dispatch("classes/getClass", { organizationId: this.organizationId, groupId: this.classId });
      const hash_url = this.$route.hash;
      switch (hash_url) {
          case "#overview":
            this.activeEntry = '1';
            this.activeComponent = "overview";
            break;
          case "#detail":
            this.activeEntry = '2';
            this.activeComponent = 'class-students';
            break;
          case "#trackingtime":
            this.activeEntry = '3';
            this.activeComponent = 'class-reports';
            break;
          case "#assignment":
            this.activeEntry = '4';
            this.activeComponent = 'assignment-list';
            break;
          case "#assessment":
            this.activeEntry = '5';
            this.activeComponent = 'assessment-list';
            break;
          case "#airoleplaylog":
            this.activeEntry = '6';
            this.activeComponent = 'conversation-list';
            break;
          default:
            this.activeEntry = '1';
            this.activeComponent = "overview";
            break;
      }
    },
    destroyed() {
      this.$store.dispatch("classes/resetBuildingClass");
    },
    methods: {
      editClass() {
        const buildingClass = {
          className: this.getCurrentClass.name,
          startDate: this.getCurrentClass.start_date,
          endDate: this.getCurrentClass.end_date,
          teacher: pathOr("", ["owner", "email"])(this.getCurrentClass),
          organization: this.getCurrentClass.organization_id,
          id: this.getCurrentClass.id,
          daysPractice: pathOr(5, ["assignment", "practice_days_per_week"])(this.getCurrentClass),
          minutesPractice: pathOr(10, ["assignment", "practice_minutes_per_day"])(this.getCurrentClass),
          takeAssessment: !!path(["assignment", "test_before_class_stats"])(this.getCurrentClass),
          engagementDays: pathOr(3, ["assignment", "test_before_class_stats"])(this.getCurrentClass),
        };
        this.updateBuildingClass(buildingClass);
        this.editMode = true;
      },
      handleMenuVisibility(isVisible) {
        this.isMenuVisible = isVisible;
      },
      backToList() {
        this.$router.push({
          name: "classes",
          params: {
            organizationId: this.currentOrganizationId,
          },
        });
      },
      async editClassSubmit() {
        try {
          await this.updateClass();
          successMessage(this.$t("classes.success_class_updated"))(this);
          this.editMode = false;
        } catch (error) {
          this.errors = error;
        }
      },
      handleCancelClick() {
        this.editMode = false;
      },
      openStudents() {
        this.$refs.studentsEntry.handleClick();
      },
      onMenuChange(index) {
        switch (index) {
          case "1":
            this.activeComponent = "overview";
            this.$router.push({ name: 'class-details', hash: '#overview' })
            break;
          case "2":
            this.activeComponent = "class-students";
            this.$router.push({ name: 'class-details', hash: '#detail' })
            break;
          case "3":
            this.activeComponent = "class-reports";
            this.$router.push({ name: 'class-details', hash: '#trackingtime' })
            break;
          case "4":
            this.activeComponent = "assignment-list";
            this.$router.push({ name: 'class-details', hash: '#assignment' })
            break;
          case "5":
            this.activeComponent = "assessment-list";
            this.$router.push({ name: 'class-details', hash: '#assessment' })
            break;
          case "6":
            this.activeComponent = "conversation-list";
            this.$router.push({ name: 'class-details', hash: '#airoleplaylog' })
            break;
          default:
            this.activeComponent = "overview";
        }
      },
      goToTabIndex(index) {
        this.activeEntry = index;
        this.onMenuChange(index.toString());
      },
      ...mapActions({
        getClass: "classes/getClass",
        updateClass: "classes/updateClass",
        updateBuildingClass: "classes/updateBuildingClass",
      }),
      setNewDate(dateObject) {
        // this.internalTimelineLabel = `${formatedStartDate} - ${formatedEndDate}`
        this.startDate = dateObject.startDate;
        this.endDate = dateObject.endDate;
        // this.timelineLabel = `${formatedStartDate} - ${formatedEndDate}`
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../scss/_colors.scss";
  .class-detail {
    overflow: hidden;
    width: 100%;
  }
  @media only screen and (max-width: 992px) {
    .component-wrapper {
      margin-top: 30px;
    }
  }

  h1 {
    border-bottom: none;
    display: flex;
    align-items: center;
    span {
      color: $darker-blue;
      margin: 0 10px;
      font-size: 25px;
      &.active {
        color: $blue;
      }
      &.finished {
        color: $success;
      }
      &.draft {
        opacity: 0.5;
      }
    }
    i {
      cursor: pointer;
    }
  }

  .el-menu.sidemenu {
    .el-menu-item {
      border: none;
      span {
        text-transform: uppercase;
      }
      svg {
        width: 30px;
      }
    }
  }
  .grey-container {
    background: $lighter-gray;
    padding: 32px;
    margin-top: 32px;
  }

  .generic-error-section {
    margin-top: 0;
    margin-bottom: 30px;
  }
  .organization-container {
    padding: 40px 40px;
  }
</style>
